import { useState } from "react";
import { BsFillCaretRightFill } from "react-icons/bs";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import MainLoader from "../../../components/loading/MainLoader";
import axios from "axios";
import { IProfileData, PROFILE_ACTIONS } from "../../../interfaces/profileData";
import { getProfileData } from "../../../services/dashboard";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import AlertModal from "../../../components/alertModal/alertModal";
import { BASE_URL } from "../../../const/URL";
import SearchBar from "../../../components/searchBar/SearchBar";
import { Row, Col } from "react-bootstrap";
import ConfirmRecurrentAlert from "../../../components/alerts/ConfirmRecurrentAlert";

interface AddOn {
  id: number;
  name: string;
  description: string;
  postprice: string;
  preprice: string;
  icon_url: string;
  recurring: boolean;
  id_recurrent?: number;
}

interface Category {
  category: string;
  addons: AddOn[];
}

const DataAddOns = () => {
  const [addOnsList, setAddOnsList] = useState<Category[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [categoryList, setCategoryList] = useState<string[]>([]);
  const [confirmShow, setConfirmShow] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState<{
    name: string;
    id: number;
    recurrentId?: number;
  }>({ name: "", id: 0 });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecurrent, setIsRecurrent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState<IProfileData>();
  const [serviceParamID, setServiceParamID] = useState("");
  const [hasPrivilege, setHasPrivilege] = useState(true);
  const [showAlert, setShowAlert] = useState(false);

  const fetchProfileDataAndAddons = async (circuitId: string) => {
    try {
      setIsLoading(true);
      setShowAlert(false);

      const profile = await getProfileData(circuitId);

      if (
        !profile.priviledges ||
        profile.priviledges[PROFILE_ACTIONS.DATA_ADDONS] !== "true"
      ) {
        setHasPrivilege(false);
        setError("You don't have permission to access data addons");
        setShowAlert(true);
        return;
      }

      if (!profile.subscriberid || !profile.subscriber_Package) {
        setError("Missing required subscriber data");
        setShowAlert(true);
        return;
      }

      setHasPrivilege(true);
      setProfileData(profile);
      await fetchDataAddOns(profile);
    } catch (error) {
      setHasPrivilege(false);
      setError(
        error instanceof Error ? error.message : "Failed to fetch profile data"
      );
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataAddOns = async (profileData: IProfileData) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("enterprise_portal_access_Token");

      const requestBody = {
        subscriberId: profileData.subscriberid,
        basePackage: profileData.subscriber_Package,
      };

      const response = await axios.post(
        `${BASE_URL}/api/v1/ENTAddOnsManagement/api/GetSubscriberAddonsList/AddonsList`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const categories = response.data.categories;
      setAddOnsList(categories);

      const catList = categories.map((c: any) => c.category.trim());
      if (catList.includes("Independence Day Free Offer")) {
        setSelectedType("Independence Day Free Offer");
        catList.unshift(catList.pop()!);
      } else {
        setSelectedType(catList[0]);
      }
      setCategoryList(catList);
    } catch (err) {
      setError("Failed to fetch data add-ons");
    } finally {
      setIsLoading(false);
    }
  };

  const selectAddOns = (categoryType: string): AddOn[] => {
    if (!addOnsList.length) return [];

    const category = addOnsList.find((c) => c.category.trim() === categoryType);
    if (!category) return [];

    return category.addons.reduce<AddOn[]>((acc, addon) => {
      if (addon.name.includes("One Time")) {
        const recurrent = category.addons.find(
          (a) =>
            a.name.includes("Recurrent") &&
            a.name.includes(addon.name.split(" One Time")[0])
        );

        if (recurrent) {
          return [...acc, { ...addon, id_recurrent: recurrent.id }];
        }
      }
      if (!addon.name.includes("Recurrent")) {
        return [...acc, addon];
      }
      return acc;
    }, []);
  };

  const handleAddOnClick = (addon: AddOn) => {
    setSelectedAddOn({
      name: addon.name,
      id: addon.id,
      recurrentId: addon.id_recurrent,
    });
    setConfirmShow(true);
  };

  const handleConfirmPurchase = async (isRecurrent: boolean) => {
    const addOnId = isRecurrent ? selectedAddOn.recurrentId : selectedAddOn.id;
    if (!addOnId || !profileData) {
      setError("Invalid add-on selection or missing required data");
      setShowAlert(true);
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem("enterprise_portal_access_Token");

      const requestBody = {
        subscriberId: profileData.subscriberid,
        packageID: addOnId.toString(),
        commitUser: profileData.subscriberid,
        channel: "ENTPortal",
      };

      const response = await axios.post(
        `${BASE_URL}/api/v1/ENTAddOnsManagement/api/GetSubscriberAddonsList/ActiveAddons`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        setSuccess(true);
        await fetchProfileDataAndAddons(serviceParamID);
      } else {
        throw new Error("Failed to activate add-on");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to purchase add-on"
      );
      setShowAlert(true);
    } finally {
      setIsLoading(false);
      setConfirmShow(false);
    }
  };

  const handleSearch = async (searchTerm: string) => {
    setServiceParamID(searchTerm);
    if (!searchTerm) {
      return;
    }

    try {
      setIsLoading(true);
      await fetchProfileDataAndAddons(searchTerm);
    } catch (error) {
      setError("Failed to fetch data for the provided ID");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container">
      <MainLoader show={isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/serviceRequest/dataAddon",
            title: "Data Addon",
          },
        ]}
      />

      <AlertModal
        show={showAlert}
        variant="error"
        buttons={[
          {
            text: "Ok",
            onClick: () => {
              setShowAlert(false);
            },
            variant: "success",
          },
        ]}
      >
        {error}
      </AlertModal>

      <AlertModal
        show={success}
        variant="success"
        buttons={[
          {
            text: "Ok",
            onClick: () => setSuccess(false),
            variant: "success",
          },
        ]}
      >
        You have successfully purchased the {selectedAddOn.name}{" "}
        {isRecurrent ? "(Recurrent)" : ""} add-on
      </AlertModal>

      <div className="dataAddOns common-panel">
        {confirmShow && (
          <ConfirmRecurrentAlert
            setConfirmShow={setConfirmShow}
            onClick={handleConfirmPurchase}
            msg={
              selectedType === "Entertainment Unlimited" ||
              selectedType === "Gaming" ||
              selectedType === "YouTube" ||
              selectedType === "Messaging"
                ? `Do you want to purchase and activate auto renewing ${selectedAddOn.name} Add-on?`
                : selectedType === "Seasonal Offer"
                ? "Do you want to redeem free data bundle?"
                : `Do you want to purchase and activate ${selectedAddOn.name}?`
            }
            pId={selectedAddOn.id}
            rId={selectedAddOn.recurrentId}
            setIsRecurrent={setIsRecurrent}
            isRecurrent={isRecurrent}
          />
        )}

        <Row className="justify-content-center mt-2 mb-5">
          <Col className="col-md-5">
            <SearchBar
              onSearch={handleSearch}
              defaultValue={serviceParamID}
              placeholder="Broadband ID"
            />
          </Col>
        </Row>

        {!hasPrivilege && <EmptyPlaceholder className="mt-5" />}

        {hasPrivilege && (
          <div>
            {!serviceParamID ? (
              <EmptyPlaceholder className="mt-5" />
            ) : (
              <div className="addon-container">
                <div className="leftSide">
                  {categoryList.map((category, index) => (
                    <div
                      key={index}
                      className="buttonTab-wrapper"
                      hidden={selectAddOns(category).length === 0}
                    >
                      <div
                        className={`buttonTab ${
                          selectedType === category ? "blue" : ""
                        }`}
                        onClick={() => setSelectedType(category)}
                      >
                        <div className="text-center">
                          <p className="m-2">{category}</p>
                        </div>
                        {selectedType === category && (
                          <BsFillCaretRightFill size={20} color="#007ed6" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rightSide">
                  <div className="addon-items clearfix">
                    {selectedType &&
                      selectAddOns(selectedType).map((addon, index) => (
                        <div
                          key={index}
                          className="item-container"
                          onClick={() => handleAddOnClick(addon)}
                        >
                          <div className="item">
                            <div className="title">{addon.name}</div>
                            <div
                              className="banner-container"
                              style={{
                                backgroundImage: `url(${addon.icon_url})`,
                              }}
                            >
                              <div className="priceTag">
                                <div>{addon.description}</div>
                                <div>
                                  {selectedType === "Seasonal Hamper"
                                    ? "Free"
                                    : `Rs. ${addon.postprice}`}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataAddOns;
